import { useEffect, useMemo, useState } from "react";
import { Wallet, Plus, Loader2, Ban } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { todayISO } from "@/lib/dateFormat";

/** Adelantos (saldo a favor): plata que el estudiante pagó antes de que naciera
 *  el ciclo. El sistema la aplica sola al cobro nuevo, así que a esa persona no
 *  le sale ningún correo de cobro. */
export const AdvancesPanel = ({ onApplied }) => {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [query, setQuery] = useState("");
  const [students, setStudents] = useState([]);
  const [form, setForm] = useState({
    student: null, amount: "", received_date: todayISO(),
    payment_method: "deposit", notes: "", notify_student: true,
  });

  const load = async () => {
    try {
      const r = await api.get("/advances");
      setData(r.data);
    } catch {
      setData({ advances: [], open_count: 0, open_total: 0 });
    }
  };
  useEffect(() => { load(); }, []);

  useEffect(() => {
    if (!open || query.trim().length < 2) { setStudents([]); return; }
    const id = setTimeout(async () => {
      try {
        const r = await api.get("/users");
        const q = query.trim().toLowerCase();
        setStudents((r.data || [])
          .filter((u) => u.role === "student"
            && (`${u.name || ""} ${u.email || ""}`).toLowerCase().includes(q))
          .slice(0, 6));
      } catch { setStudents([]); }
    }, 250);
    return () => clearTimeout(id);
  }, [query, open]);

  const abiertos = useMemo(
    () => (data?.advances || []).filter((a) => a.status === "open"), [data]);

  const guardar = async () => {
    if (!form.student) return toast.error("Elegí al estudiante");
    const monto = Number(form.amount);
    if (!monto || monto <= 0) return toast.error("Poné el monto del adelanto");
    setSaving(true);
    try {
      const r = await api.post("/advances", {
        student_id: form.student.user_id,
        amount: monto,
        received_date: form.received_date,
        payment_method: form.payment_method,
        notes: form.notes,
        notify_student: form.notify_student,
      });
      toast.success(r.data?.applied_to_payment
        ? "Adelanto registrado y aplicado a su factura pendiente"
        : "Adelanto registrado: se aplicará solo al próximo ciclo");
      setOpen(false);
      setForm({ student: null, amount: "", received_date: todayISO(),
                payment_method: "deposit", notes: "", notify_student: true });
      setQuery("");
      await load();
      onApplied?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo registrar el adelanto");
    } finally {
      setSaving(false);
    }
  };

  const anular = async (a) => {
    const reason = window.prompt(`Anular el adelanto de ${a.student_name} por $${a.available.toFixed(2)}. ¿Por qué?`);
    if (!reason) return;
    try {
      await api.post(`/advances/${a.id}/annul`, { reason });
      toast.success("Adelanto anulado");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo anular");
    }
  };

  return (
    <>
      <div className="mb-3 flex flex-wrap items-center gap-3 p-3 rounded-lg border border-teal-200 bg-teal-50 text-sm"
           data-testid="advances-toolbar">
        <Wallet className="h-4 w-4 text-teal-600 flex-shrink-0" />
        <div className="flex-1 min-w-0">
          {abiertos.length === 0 ? (
            <span className="text-teal-900" data-testid="advances-empty">
              Sin pagos adelantados pendientes de aplicar.
            </span>
          ) : (
            <>
              <span className="font-semibold text-teal-900" data-testid="advances-summary">
                {abiertos.length} {abiertos.length === 1 ? "pago adelantado" : "pagos adelantados"} por $ {(data?.open_total || 0).toFixed(2)}
              </span>
              <span className="text-teal-800 ml-2">
                — se aplican solos a la factura del ciclo nuevo y a esa persona no le sale ningún cobro.
              </span>
            </>
          )}
        </div>
        <Button size="sm" variant="outline" className="border-teal-300 text-teal-800 hover:bg-teal-100"
                onClick={() => setOpen(true)} data-testid="new-advance-button">
          <Plus className="h-4 w-4" /> Registrar adelanto
        </Button>
      </div>

      {abiertos.length > 0 && (
        <ul className="mb-4 space-y-1.5" data-testid="advances-list">
          {abiertos.map((a) => (
            <li key={a.id} data-testid={`advance-row-${a.id}`}
                className="flex items-center justify-between gap-3 text-sm bg-white border border-teal-100 rounded px-3 py-2">
              <span className="truncate">
                <span className="font-medium text-slate-800">{a.student_name}</span>
                <span className="text-slate-500"> · {a.received_date}</span>
                {a.notes && <span className="text-slate-400"> · {a.notes}</span>}
              </span>
              <span className="flex items-center gap-3 whitespace-nowrap">
                <span className="font-semibold text-teal-700">$ {a.available.toFixed(2)}</span>
                <button onClick={() => anular(a)} data-testid={`annul-advance-${a.id}`}
                        className="text-slate-400 hover:text-red-600" title="Anular este adelanto">
                  <Ban className="h-3.5 w-3.5" />
                </button>
              </span>
            </li>
          ))}
        </ul>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="advance-dialog">
          <DialogHeader>
            <DialogTitle>Registrar pago adelantado</DialogTitle>
            <DialogDescription>
              La plata queda como saldo a favor y se aplica sola a la factura del próximo
              ciclo. Cuenta como venta el día que se aplica.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Estudiante</Label>
              {form.student ? (
                <div className="flex items-center justify-between gap-2 border border-slate-200 rounded px-3 py-2 mt-1">
                  <span className="text-sm text-slate-800">{form.student.name}</span>
                  <button className="text-xs text-slate-500 hover:text-slate-800"
                          onClick={() => setForm((f) => ({ ...f, student: null }))}
                          data-testid="advance-student-clear">cambiar</button>
                </div>
              ) : (
                <>
                  <Input className="mt-1" placeholder="Buscar por nombre…" value={query}
                         onChange={(e) => setQuery(e.target.value)}
                         data-testid="advance-student-search" />
                  {students.length > 0 && (
                    <ul className="mt-1 border border-slate-200 rounded overflow-hidden">
                      {students.map((s) => (
                        <li key={s.user_id}>
                          <button className="w-full text-left px-3 py-2 text-sm hover:bg-slate-50"
                                  data-testid={`advance-student-${s.user_id}`}
                                  onClick={() => { setForm((f) => ({ ...f, student: s })); setStudents([]); }}>
                            {s.name} <span className="text-slate-400 text-xs ml-1">{s.email}</span>
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </>
              )}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Monto (USD)</Label>
                <Input className="mt-1" type="number" min="0" step="0.01" value={form.amount}
                       onChange={(e) => setForm((f) => ({ ...f, amount: e.target.value }))}
                       data-testid="advance-amount" />
              </div>
              <div>
                <Label>Fecha del depósito</Label>
                <Input className="mt-1" type="date" value={form.received_date}
                       onChange={(e) => setForm((f) => ({ ...f, received_date: e.target.value }))}
                       data-testid="advance-date" />
              </div>
            </div>
            <div>
              <Label>Forma de pago</Label>
              <Select value={form.payment_method}
                      onValueChange={(v) => setForm((f) => ({ ...f, payment_method: v }))}>
                <SelectTrigger className="mt-1" data-testid="advance-method">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="deposit">Depósito / transferencia</SelectItem>
                  <SelectItem value="card">Tarjeta</SelectItem>
                  <SelectItem value="cash">Efectivo</SelectItem>
                  <SelectItem value="unspecified">Sin especificar</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Nota (opcional)</Label>
              <Input className="mt-1" value={form.notes}
                     onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
                     placeholder="Pagó al recibir el aviso del 75 %"
                     data-testid="advance-notes" />
            </div>
            <div className="flex items-center justify-between border-t border-slate-100 pt-3">
              <span className="text-sm text-slate-700">Avisarle que recibimos su pago</span>
              <Switch checked={form.notify_student} data-testid="advance-notify"
                      onCheckedChange={(v) => setForm((f) => ({ ...f, notify_student: v }))} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
            <Button onClick={guardar} disabled={saving} data-testid="advance-save">
              {saving && <Loader2 className="h-4 w-4 animate-spin" />} Registrar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
